import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus, Building2, MapPin, Users, Loader2, Pencil, Trash2,
  Layers, Coffee, Dumbbell, Waves, TreePine, Car, UtensilsCrossed,
  Presentation, Wine, Sun, Armchair, X, AlertTriangle, Upload, Download,
  Boxes
} from 'lucide-react';

const ALL_COMMON_AREAS = [
  { id: 'Hall', label: 'Hall / Lobby', icon: Building2 },
  { id: 'Reception', label: 'Reception', icon: Armchair },
  { id: 'Ristorante', label: 'Ristorante', icon: UtensilsCrossed },
  { id: 'Bar', label: 'Bar / Lounge', icon: Wine },
  { id: 'Sala Conferenze', label: 'Sala Conferenze', icon: Presentation },
  { id: 'Spa', label: 'Spa / Wellness', icon: Coffee },
  { id: 'Piscina', label: 'Piscina', icon: Waves },
  { id: 'Palestra', label: 'Palestra / Fitness', icon: Dumbbell },
  { id: 'Giardino', label: 'Giardino', icon: TreePine },
  { id: 'Terrazza', label: 'Terrazza', icon: Sun },
  { id: 'Parcheggio', label: 'Parcheggio', icon: Car },
];

const HotelsPage = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editHotel, setEditHotel] = useState(null);
  const [customArea, setCustomArea] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', address: '', city: '', cluster: '', num_rooms: '', floors: '',
    common_areas: ['Hall', 'Reception'], rooms: []
  });

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/hotels');
      setHotels(data);
    } catch (err) {
      toast.error('Errore nel caricamento degli hotel');
    } finally {
      setLoading(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setImporting(true);
      const { data } = await api.post('/hotels/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      if (data.errors?.length > 0) {
        data.errors.forEach(err => toast.warning(err));
      }
      fetchHotels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'importazione');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/hotels/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hostfix_hotel_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore nel download template');
    }
  };

  useEffect(() => { fetchHotels(); }, []);

  // Generate rooms automatically from floors count
  const autoGenerateRooms = (numFloors, roomsPerFloor, startFloor = 1) => {
    const rooms = [];
    for (let f = startFloor; f < startFloor + numFloors; f++) {
      const floorLabel = f < 0 ? `S${Math.abs(f)}` : f.toString();
      for (let r = 1; r <= roomsPerFloor; r++) {
        rooms.push({ number: `${floorLabel}${r.toString().padStart(2, '0')}`, floor: f });
      }
    }
    return rooms;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        num_rooms: parseInt(formData.num_rooms) || 0,
        floors: parseInt(formData.floors) || 1,
        common_areas: formData.common_areas,
        rooms: formData.rooms
      };
      if (editHotel) {
        await api.put(`/hotels/${editHotel.id}`, payload);
        toast.success('Hotel aggiornato');
      } else {
        await api.post('/hotels', payload);
        toast.success('Hotel creato');
      }
      setCreateOpen(false);
      setEditHotel(null);
      resetForm();
      fetchHotels();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', city: '', cluster: '', num_rooms: '', floors: '', common_areas: ['Hall', 'Reception'], rooms: [] });
    setCustomArea('');
  };

  const handleEdit = (hotel) => {
    setEditHotel(hotel);
    setFormData({
      name: hotel.name,
      address: hotel.address,
      city: hotel.city,
      cluster: hotel.cluster || '',
      num_rooms: hotel.num_rooms?.toString() || '',
      floors: hotel.floors?.toString() || '',
      common_areas: hotel.common_areas || [],
      rooms: hotel.rooms || []
    });
    setCreateOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/hotels/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} eliminato`);
      setDeleteTarget(null);
      fetchHotels();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const toggleArea = (areaId) => {
    setFormData(prev => ({
      ...prev,
      common_areas: prev.common_areas.includes(areaId)
        ? prev.common_areas.filter(a => a !== areaId)
        : [...prev.common_areas, areaId]
    }));
  };

  const addCustomArea = () => {
    const trimmed = customArea.trim();
    if (trimmed && !formData.common_areas.includes(trimmed)) {
      setFormData(prev => ({ ...prev, common_areas: [...prev.common_areas, trimmed] }));
      setCustomArea('');
    }
  };

  const removeCustomArea = (area) => {
    setFormData(prev => ({ ...prev, common_areas: prev.common_areas.filter(a => a !== area) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hotels-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Hotel</h1>
          <p className="text-slate-500 mt-1">{hotels.length} strutture</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-button">
            <Download className="w-4 h-4 mr-1.5" /> Template CSV
          </Button>
          <label>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImportFile} className="hidden" data-testid="import-file-input" />
            <Button variant="outline" size="sm" asChild disabled={importing}>
              <span className="cursor-pointer">
                {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Importa
              </span>
            </Button>
          </label>
          <Dialog open={createOpen} onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) { setEditHotel(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="create-hotel-button">
              <Plus className="w-4 h-4" /> Nuovo Hotel
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editHotel ? 'Modifica Hotel' : 'Nuovo Hotel'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Grand Hotel Milano" required data-testid="hotel-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Indirizzo *</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Via Roma 1" required data-testid="hotel-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Citta *</Label>
                  <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="Milano" required data-testid="hotel-city-input" />
                </div>
                <div className="space-y-2">
                  <Label>Cluster</Label>
                  <Input value={formData.cluster} onChange={(e) => setFormData({...formData, cluster: e.target.value})} placeholder="Milano" data-testid="hotel-cluster-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Numero Camere *</Label>
                  <Input type="number" value={formData.num_rooms} onChange={(e) => setFormData({...formData, num_rooms: e.target.value})} placeholder="100" required data-testid="hotel-rooms-input" />
                </div>
                <div className="space-y-2">
                  <Label>Piani *</Label>
                  <Input type="number" value={formData.floors} onChange={(e) => setFormData({...formData, floors: e.target.value})} placeholder="5" required data-testid="hotel-floors-input" />
                </div>
              </div>

              {/* Common Areas Section */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <Label className="text-base font-bold flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-900" />
                  Aree Comuni
                </Label>
                <p className="text-xs text-slate-500">Seleziona le aree comuni presenti in questo hotel</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ALL_COMMON_AREAS.map(area => {
                    const Icon = area.icon;
                    const checked = formData.common_areas.includes(area.id);
                    return (
                      <button
                        type="button"
                        key={area.id}
                        onClick={() => toggleArea(area.id)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left text-sm transition-all ${
                          checked
                            ? 'border-blue-900 bg-blue-50 text-blue-900'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid={`area-toggle-${area.id.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className={`w-4 h-4 flex-shrink-0 ${checked ? 'text-blue-900' : 'text-slate-400'}`} />
                        <span className="font-medium">{area.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Custom areas already added */}
                {formData.common_areas.filter(a => !ALL_COMMON_AREAS.find(p => p.id === a)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.common_areas.filter(a => !ALL_COMMON_AREAS.find(p => p.id === a)).map(area => (
                      <Badge key={area} className="bg-blue-100 text-blue-900 gap-1 px-3 py-1.5">
                        {area}
                        <button type="button" onClick={() => removeCustomArea(area)} className="ml-1 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Add custom area */}
                <div className="flex gap-2 mt-1">
                  <Input
                    value={customArea}
                    onChange={(e) => setCustomArea(e.target.value)}
                    placeholder="Aggiungi area personalizzata..."
                    className="flex-1"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomArea(); }}}
                    data-testid="custom-area-input"
                  />
                  <Button type="button" variant="outline" onClick={addCustomArea} disabled={!customArea.trim()}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Rooms Management Section */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <Label className="text-base font-bold flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-900" />
                  Camere ({formData.rooms.length})
                </Label>
                <p className="text-xs text-slate-500">Configura le camere reali dell'hotel. Puoi generare automaticamente o aggiungere manualmente.</p>
                
                {/* Auto generate */}
                <div className="flex items-end gap-2 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <Label className="text-xs">Piano di partenza</Label>
                    <Input
                      type="number"
                      id="startFloor"
                      placeholder="1"
                      defaultValue="1"
                      className="h-9"
                      min="-2" max="50"
                      data-testid="start-floor-input"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Camere per piano</Label>
                    <Input
                      type="number"
                      id="roomsPerFloor"
                      placeholder="20"
                      className="h-9"
                      min="1" max="50"
                      data-testid="rooms-per-floor-input"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 text-xs"
                    onClick={() => {
                      const rpf = parseInt(document.getElementById('roomsPerFloor').value) || 20;
                      const fl = parseInt(formData.floors) || 1;
                      const sf = parseInt(document.getElementById('startFloor').value) || 1;
                      const generated = autoGenerateRooms(fl, rpf, sf);
                      setFormData(prev => ({ ...prev, rooms: generated, num_rooms: generated.length.toString() }));
                      toast.success(`${generated.length} camere generate (piano ${sf} a ${sf + fl - 1})`);
                    }}
                    data-testid="auto-generate-rooms-btn"
                  >
                    Genera Automaticamente
                  </Button>
                </div>

                {/* Manual add room */}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Numero camera</Label>
                    <Input id="newRoomNumber" placeholder="es. 301A" className="h-9" />
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Piano</Label>
                    <Input id="newRoomFloor" type="number" placeholder="0" className="h-9" min="-1" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9"
                    onClick={() => {
                      const num = document.getElementById('newRoomNumber').value.trim();
                      const fl = parseInt(document.getElementById('newRoomFloor').value) || 1;
                      if (!num) return;
                      if (formData.rooms.find(r => r.number === num)) {
                        toast.error('Camera gia presente');
                        return;
                      }
                      setFormData(prev => ({
                        ...prev,
                        rooms: [...prev.rooms, { number: num, floor: fl }].sort((a, b) => a.floor - b.floor || a.number.localeCompare(b.number)),
                        num_rooms: (prev.rooms.length + 1).toString()
                      }));
                      document.getElementById('newRoomNumber').value = '';
                      document.getElementById('newRoomFloor').value = '';
                    }}
                    data-testid="add-room-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Rooms list by floor */}
                {formData.rooms.length > 0 && (
                  <div className="border border-slate-200 rounded-lg">
                    <div className="overflow-y-scroll" style={{ maxHeight: '200px', scrollbarWidth: 'thin' }}>
                      <div className="space-y-2 p-3">
                        {Array.from(new Set(formData.rooms.map(r => r.floor))).sort((a, b) => a - b).map(floor => (
                          <div key={floor} className="space-y-1">
                            <p className="text-xs font-bold text-slate-500 uppercase sticky top-0 bg-white py-0.5">Piano {floor}</p>
                            <div className="flex flex-wrap gap-1">
                              {formData.rooms.filter(r => r.floor === floor).map(room => (
                                <Badge key={room.number} variant="outline" className="text-xs gap-1 py-1 px-2 bg-white">
                                  {room.number}
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({
                                      ...prev,
                                      rooms: prev.rooms.filter(r => r.number !== room.number),
                                      num_rooms: (prev.rooms.length - 1).toString()
                                    }))}
                                    className="ml-0.5 text-slate-400 hover:text-red-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
                <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="hotel-submit-button">
                  {editHotel ? 'Salva Modifiche' : 'Crea Hotel'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Hotels grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hotels.map((hotel) => (
          <Card key={hotel.id} className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`hotel-card-${hotel.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-900" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-slate-900">{hotel.name}</CardTitle>
                    {hotel.cluster && (
                      <Badge variant="secondary" className="mt-1 text-xs">Cluster: {hotel.cluster}</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400" />
                {hotel.address}, {hotel.city}
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-slate-600">
                  <Users className="w-4 h-4 text-slate-400" /> {hotel.num_rooms} camere
                </span>
                <span className="text-slate-400">|</span>
                <span className="text-slate-600">{hotel.floors} piani</span>
              </div>
              {/* Common areas tags */}
              {hotel.common_areas && hotel.common_areas.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {hotel.common_areas.map(area => (
                    <Badge key={area} variant="outline" className="text-[10px] py-0.5 px-2 text-slate-500 border-slate-200">
                      {area}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => handleEdit(hotel)} className="flex-1" data-testid={`edit-hotel-${hotel.id}`}>
                  <Pencil className="w-4 h-4 mr-1" /> Modifica
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDeleteTarget(hotel)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-hotel-${hotel.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {hotels.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessun hotel presente</p>
        </div>
      )}

      {/* Delete Warning Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-red-700">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              Eliminazione Hotel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-sm text-red-800 font-semibold mb-1">
                Stai per eliminare definitivamente:
              </p>
              <p className="text-lg font-bold text-red-900">{deleteTarget?.name}</p>
              {deleteTarget?.address && (
                <p className="text-xs text-red-700 mt-1">{deleteTarget?.address}, {deleteTarget?.city}</p>
              )}
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">Questa azione comporta:</p>
              <ul className="space-y-1.5 pl-1">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5 font-bold">-</span>
                  Eliminazione di tutte le <strong>{deleteTarget?.num_rooms || 0} camere</strong> configurate
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5 font-bold">-</span>
                  I ticket associati a questo hotel perderanno il riferimento
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5 font-bold">-</span>
                  Le manutenzioni contrattuali legate a questo hotel non saranno piu visibili
                </li>
              </ul>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800 font-semibold">
                Questa operazione non puo essere annullata.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-hotel">
              Annulla
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-hotel"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Elimina Definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotelsPage;
